import { UploadOutlined } from '@ant-design/icons';
import { Button, Divider, Modal, Tooltip } from 'antd';
import Upload from 'antd/lib/upload';
import clsx from 'clsx';
import { useContext } from 'react';
import { HuePicker } from 'react-color';
import { isEmpty } from 'underscore';
import IconPoint from '../../../assets/icon/locate2.png';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import { useActiveMarker } from '../../../store/markerStore';
import { withLoading } from '../../common/Loading/withLoading';
import MenuSection from './component/Display/LegendView';
import ShapeFileLayers from './component/Display/ShapeFile';
import SurfaceMapRadioGroup from './component/Display/SurfaceMapRadio';
import ToggleItem from './component/Display/ToogleItem';
import UploadedLayers from './component/Display/UploadedFile';
import { useLayerControls } from './hooks/useLayerControls';
import { usePointLayerControl } from './hooks/usePointLayerControl';
import { useShapefileUpload } from './hooks/useShapeFileUpload';
import { useSurfaceMap } from './hooks/useSurfaceMapControl';
import styles from './Style/LayerOverview.module.scss';

export interface LayerOverviewProps {
  onMeshControl: (value: string) => any;
  result: any;
  setResult: any;
  legendStartEndValues: [number | null, number | null];
}

export default function LayerOverview(props: LayerOverviewProps) {
  const activeMarker = useActiveMarker();
  const loadingContext = useContext(LoadingContext);
  const point = activeMarker;

  const {
    fileList,
    uploadProps,
    uploadedFeatures,
    visibleUploads,
    uploadingShapefile,
    currentLayerColor,

    setCurrentLayerColor,
    handleOnUpload,
    onChangeUploadVisibility,
    deleteUpload,
    onCloseColorPickerDialog,
    applyColor,
    changeColor,
  } = useShapefileUpload();

  const {
    showPointSection,
    setShowPointsSection,
    showLabelLayer,
    showFWEPoints,
    showInSituPoints,
    isHavePoint,
    isHaveFwePoint,
    isHaveInsituPoint,
    isHave2DSpectra,
    updateShowLabels,
    setShowFWEPoints,
    setShowInSituPoints,
  } = usePointLayerControl();

  const {
    showLayerSection,
    setShowLayerSection,
    showWavesZones,
    setShowWavesZones,
    showAtmosphereZones,
    setShowAtmosphereZones,
    showOcean3dZones,
    setShowOcean3dZones,
    showOceanZones,
    setShowOceanZones,
    showGlobalShapeFile,
    setShowGlobalShapeFile,
    showMesh,
    updateMeshSwitch,
    updateShowShape,
    showModelDomainsSection,
    setShowModelDomainsSection,
  } = useLayerControls(
    props.onMeshControl,
    activeMarker,
    !!isHavePoint,
    !!isHave2DSpectra
  );

  const { showSurfaceMapSection, setShowSurfaceMapSection, handleSurfaceMap } =
    useSurfaceMap(
      props.result,
      props.setResult,
      props.legendStartEndValues,
      point
    );

  return (
    <div className={`${styles.root}`} data-testid='Layers-box'>
      <Modal
        open={!isEmpty(currentLayerColor)}
        title='Select a color'
        onCancel={onCloseColorPickerDialog}
        footer={<Button onClick={onCloseColorPickerDialog}>OK</Button>}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <HuePicker
          color={currentLayerColor}
          onChange={(color) => setCurrentLayerColor(color.hex)}
          onChangeComplete={(color) => applyColor(color)}
        />
      </Modal>

      <MenuSection
        title='Points'
        isExpanded={showPointSection}
        onToggle={() => setShowPointsSection(!showPointSection)}
      >
        <ToggleItem
          label='User Points'
          icon={
            <img
              src={IconPoint}
              alt={''}
              width={30}
              height={30}
              style={{ position: 'relative', right: '6px' }}
            />
          }
          labelStyle={{ position: 'relative', right: '7px' }}
          disabled={!isHavePoint}
          isActive={showLabelLayer}
          onToggle={() => updateShowLabels(!showLabelLayer)}
        />

        <ToggleItem
          label='In-Situ Station'
          iconStyle={styles.circleInSitu}
          disabled={!isHaveInsituPoint}
          isActive={showInSituPoints}
          onToggle={() => setShowInSituPoints(!showInSituPoints)}
        />

        <ToggleItem
          label='Local Model'
          iconStyle={styles.circle}
          disabled={!isHaveFwePoint}
          isActive={showFWEPoints}
          onToggle={() => setShowFWEPoints(!showFWEPoints)}
        />
      </MenuSection>

      <MenuSection
        title='Model Domains'
        isExpanded={showModelDomainsSection}
        onToggle={() => setShowModelDomainsSection(!showModelDomainsSection)}
      >
        <ToggleItem
          label='Waves'
          iconStyle={clsx(styles.boxPerimeter, styles.boxWaves)}
          isActive={showWavesZones}
          onToggle={() => setShowWavesZones(!showWavesZones)}
        />

        <ToggleItem
          label='Ocean'
          iconStyle={clsx(styles.boxPerimeter, styles.boxOcean)}
          isActive={showOceanZones}
          onToggle={() => setShowOceanZones(!showOceanZones)}
        />

        <ToggleItem
          label='Ocean-3D'
          iconStyle={clsx(styles.boxPerimeter, styles.boxOcean3d)}
          isActive={showOcean3dZones}
          onToggle={() => setShowOcean3dZones(!showOcean3dZones)}
        />

        <ToggleItem
          label='Atmosphere'
          iconStyle={clsx(styles.boxPerimeter, styles.boxAtmosphere)}
          isActive={showAtmosphereZones}
          onToggle={() => setShowAtmosphereZones(!showAtmosphereZones)}
        />
      </MenuSection>

      <MenuSection
        title='Layers'
        isExpanded={showLayerSection}
        onToggle={() => setShowLayerSection(!showLayerSection)}
      >
        <ToggleItem
          label='Model Grid'
          iconStyle={styles.boxGrid}
          disabled={!isHavePoint}
          isActive={showMesh}
          onToggle={() =>
            withLoading(() => updateMeshSwitch(!showMesh), loadingContext, {
              source: 'meshSwitch',
            })
          }
        />

        {point && (
          <ShapeFileLayers
            shapeFiles={point.shapeFiles}
            dataset={point.dataset}
            updateShowShape={updateShowShape}
            showDisabledMessage={point.id !== undefined && !isHave2DSpectra}
          />
        )}

        <ToggleItem
          label='Offshore Wind Farms'
          iconStyle={styles.boxOffshore}
          disabled={false}
          isActive={showGlobalShapeFile}
          onToggle={() => setShowGlobalShapeFile(!showGlobalShapeFile)}
        />

        <Divider style={{ margin: 0 }} />

        <UploadedLayers
          uploadedFeatures={uploadedFeatures}
          visibleUploads={visibleUploads}
          onChangeUploadVisibility={onChangeUploadVisibility}
          deleteUpload={deleteUpload}
          changeColor={changeColor}
        />

        <div className={styles.rowUpload}>
          <Upload
            {...uploadProps}
            className={styles.upload}
            fileList={fileList}
          >
            <Tooltip title='The following files must be uploaded together: .shp, .shx, .prj'>
              <Button
                type='primary'
                className={styles.uploadButton}
                loading={uploadingShapefile}
                onClick={handleOnUpload}
                size='small'
              >
                + Add Shape Files
                <UploadOutlined />
              </Button>
            </Tooltip>
          </Upload>
        </div>
      </MenuSection>

      {point?.id && (
        <MenuSection
          title='Surface Map'
          isExpanded={showSurfaceMapSection}
          onToggle={() => setShowSurfaceMapSection(!showSurfaceMapSection)}
        >
          <div style={{ overflowY: 'auto', maxHeight: '350px' }}>
            <SurfaceMapRadioGroup
              categories={point.surfaceMaps.categories}
              overlays={point.surfaceMaps.overlays}
              selectedValue={
                point.surfaceMaps.selectedLevel2
                  ? `${point.surfaceMaps.selectedLevel1}|${point.surfaceMaps.selectedLevel2}`
                  : 'none'
              }
              onChange={(value) => handleSurfaceMap(value, false)}
            />
          </div>
        </MenuSection>
      )}
    </div>
  );
}
